import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Avatar,
  Chip,
  IconButton,
  Stack,
  Grid,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { apiCall } from '../utils/api';

const FamilyManagement = ({ family, user, onBack, onFamilyDeleted }) => {
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [editingInvitation, setEditingInvitation] = useState(null);
  const [inviteData, setInviteData] = useState({
    email: '',
    role: 'member'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isInviting, setIsInviting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [cancelInvitationDialogOpen, setCancelInvitationDialogOpen] = useState(false);
  const [invitationToCancel, setInvitationToCancel] = useState(null);

  const isAdmin = family.user_role === 'admin';
  
  // Check if current user is the family owner - try different possible field names
  const isOwner = user.id === family.owner_id || 
                  user.id === family.created_by || 
                  user.id === family.created_by_id ||
                  (family.user_role === 'owner') ||
                  (isAdmin && family.members?.length === 1); // If admin and only member, likely the owner

  const fetchFamilyData = useCallback(async () => {
    setIsLoading(true);
    try {
      // Fetch members
      const membersData = await apiCall(`/api/families/${family.id}/members`);
      setMembers(membersData);

      // Fetch pending invitations (admin only)
      if (isAdmin) {
        const invitationsData = await apiCall(`/api/families/${family.id}/invitations`);
        setInvitations(invitationsData);
      }
    } catch (error) {
      setError('Failed to load family data');
    } finally {
      setIsLoading(false);
    }
  }, [family.id, isAdmin]);

  useEffect(() => {
    fetchFamilyData();
  }, [family.id, fetchFamilyData]);

  const handleInvite = async (e) => {
    e.preventDefault();
    setIsInviting(true);
    setError('');
    setSuccess('');

    try {
      await apiCall(`/api/families/${family.id}/invite`, {
        method: 'POST',
        body: JSON.stringify(inviteData),
      });

      setSuccess(`Invitation sent to ${inviteData.email}`);
      setInviteData({ email: '', role: 'member' });
      setShowInviteForm(false);
      fetchFamilyData(); // Refresh data
    } catch (error) {
      setError(error.message || 'Failed to send invitation');
    } finally {
      setIsInviting(false);
    }
  };

  const handleInputChange = (e) => {
    setInviteData({
      ...inviteData,
      [e.target.name]: e.target.value
    });
  };

  const handleEditInvitation = (invitation) => {
    setEditingInvitation(invitation);
    setInviteData({
      email: invitation.email,
      role: invitation.role
    });
    setShowInviteForm(true);
    setError('');
    setSuccess('');
  };

  const handleUpdateInvitation = async (e) => {
    e.preventDefault();
    setIsUpdating(true);
    setError('');
    setSuccess('');

    try {
      const updates = {};
      if (inviteData.email !== editingInvitation.email) {
        updates.email = inviteData.email;
      }
      if (inviteData.role !== editingInvitation.role) {
        updates.role = inviteData.role;
      }

      if (Object.keys(updates).length === 0) {
        setError('No changes to save');
        setIsUpdating(false);
        return;
      }

      await apiCall(`/api/families/${family.id}/invitations/${editingInvitation.id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });

      setSuccess('Invitation updated successfully');
      setInviteData({ email: '', role: 'member' });
      setEditingInvitation(null);
      setShowInviteForm(false);
      fetchFamilyData(); // Refresh data
    } catch (error) {
      setError(error.message || 'Failed to update invitation');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancelInvitation = (invitationId) => {
    setInvitationToCancel(invitationId);
    setCancelInvitationDialogOpen(true);
  };

  const handleCancelInvitationConfirm = async () => {
    try {
      await apiCall(`/api/families/${family.id}/invitations/${invitationToCancel}`, {
        method: 'DELETE',
      });

      setSuccess('Invitation cancelled successfully');
      fetchFamilyData(); // Refresh data
      setCancelInvitationDialogOpen(false);
      setInvitationToCancel(null);
    } catch (error) {
      setError(error.message || 'Failed to cancel invitation');
    }
  };

  const handleCancelInvitationCancel = () => {
    setCancelInvitationDialogOpen(false);
    setInvitationToCancel(null);
  };

  const handleDeleteFamily = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await apiCall(`/api/families/${family.id}`, {
        method: 'DELETE',
      });

      setSuccess('Family deleted successfully');
      
      // Call the callback to notify parent component that family was deleted
      if (onFamilyDeleted) {
        onFamilyDeleted(family.id);
      }
      
      setDeleteDialogOpen(false);
    } catch (error) {
      setError(error.message || 'Failed to delete family');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const resetInviteForm = () => {
    setShowInviteForm(false);
    setEditingInvitation(null);
    setError('');
    setInviteData({ email: '', role: 'member' });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
            <IconButton 
              onClick={onBack} 
              sx={{ 
                mr: 3,
                p: 1.5,
                bgcolor: 'background.paper',
                boxShadow: 2,
                '&:hover': {
                  bgcolor: 'primary.main',
                  color: 'white',
                  transform: 'scale(1.1)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              <BackIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography 
                variant="h3" 
                component="h1" 
                gutterBottom
                sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                {family.name}
              </Typography>
              {family.description && (
                <Typography 
                  variant="h6" 
                  color="text.secondary"
                  sx={{ 
                    fontWeight: 400,
                    lineHeight: 1.6,
                    maxWidth: '600px'
                  }}
                >
                  {family.description}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        {/* Alerts */}
        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              mb: 4,
              borderRadius: 2,
              '& .MuiAlert-icon': {
                fontSize: '1.5rem'
              }
            }}
          >
            {error}
          </Alert>
        )}

        {success && (
          <Alert 
            severity="success" 
            sx={{ 
              mb: 4,
              borderRadius: 2,
              '& .MuiAlert-icon': {
                fontSize: '1.5rem'
              }
            }}
          >
            {success}
          </Alert>
        )}

        <Grid container spacing={4}>
          {/* Members Section */}
          <Grid item xs={12} lg={8}>
            <Box sx={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 4,
              p: 4,
              mb: 4,
              textAlign: 'center',
              boxShadow: 3,
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
                zIndex: 1
              }
            }}>
              <Typography 
                variant="h3" 
                component="h1"
                sx={{ 
                  fontWeight: 800,
                  color: 'white',
                  textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                  mb: 1,
                  position: 'relative',
                  zIndex: 2
                }}
              >
                👨‍👩‍👧‍👦 Family Management Dashboard
              </Typography>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: 'rgba(255,255,255,0.9)',
                  fontWeight: 400,
                  position: 'relative',
                  zIndex: 2
                }}
              >
                Manage your family members, roles, and invitations
              </Typography>
            </Box>
            <Card sx={{ borderRadius: 3, boxShadow: 2 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ mb: 4 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'flex-start', 
                    mb: 3,
                    pb: 2,
                    borderBottom: '2px solid',
                    borderColor: 'divider'
                  }}>
                    <Box>
                      <Typography 
                        variant="h4" 
                        component="h2"
                        sx={{ 
                          fontWeight: 700,
                          color: 'text.primary',
                          mb: 1
                        }}
                      >
                        Family Members
                      </Typography>
                      <Typography 
                        variant="h6" 
                        color="primary.main"
                        sx={{ fontWeight: 600 }}
                      >
                        {members.length} {members.length === 1 ? 'Member' : 'Members'}
                      </Typography>
                    </Box>
                    {isAdmin && (
                      <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setShowInviteForm(true)}
                        sx={{ 
                          px: 4,
                          py: 1.5,
                          borderRadius: 3,
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '1rem',
                          boxShadow: 3,
                          '&:hover': {
                            boxShadow: 6,
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        Invite Member
                      </Button>
                    )}
                    {isOwner && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<DeleteIcon />}
                        onClick={handleDeleteFamily}
                        sx={{ 
                          px: 4,
                          py: 1.5,
                          borderRadius: 3,
                          textTransform: 'none',
                          fontWeight: 600,
                          borderColor: 'error.main',
                          color: 'error.main',
                          '&:hover': {
                            borderColor: 'error.dark',
                            backgroundColor: 'error.main',
                            color: 'white',
                            transform: 'translateY(-2px)'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        Delete Family
                      </Button>
                    )}
                  </Box>
                  <Typography 
                    variant="body1" 
                    color="text.secondary" 
                    sx={{ 
                      mb: 0,
                      fontSize: '1.1rem',
                      lineHeight: 1.6
                    }}
                  >
                    Manage your family members and their roles. Admins can invite new members and modify permissions.
                    {isOwner && (
                      <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>
                        {' '}You are the family owner and can delete this family.
                      </Box>
                    )}
                  </Typography>
                </Box>

                <Stack spacing={2.5}>
                  {members.map(member => (
                    <Paper 
                      key={member.id} 
                      variant="outlined" 
                      sx={{ 
                        p: 3,
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar 
                            sx={{ 
                              mr: 3, 
                              bgcolor: 'primary.main',
                              width: 56,
                              height: 56,
                              fontSize: '1.5rem',
                              fontWeight: 600
                            }}
                          >
                            {member.avatar_url ? (
                              <img src={member.avatar_url} alt={member.name} />
                            ) : (
                              member.name.charAt(0).toUpperCase()
                            )}
                          </Avatar>
                          <Box>
                            <Typography 
                              variant="h6" 
                              component="h3" 
                              sx={{ 
                                mb: 1,
                                fontWeight: 600,
                                color: 'text.primary'
                              }}
                            >
                              {member.name}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                mb: 1,
                                fontSize: '0.875rem'
                              }}
                            >
                              <EmailIcon sx={{ fontSize: 18, mr: 1, color: 'action.active' }} />
                              {member.email}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color="text.secondary" 
                              sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                fontSize: '0.8rem'
                              }}
                            >
                              <CalendarIcon sx={{ fontSize: 16, mr: 1, color: 'action.active' }} />
                              Joined {formatDate(member.joined_at)}
                            </Typography>
                          </Box>
                        </Box>
                        <Chip
                          label={member.role}
                          color={member.role === 'admin' ? 'primary' : 'default'}
                          variant="outlined"
                          size="medium"
                          sx={{ 
                            fontWeight: 600,
                            px: 2,
                            py: 0.5
                          }}
                        />
                      </Box>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Pending Invitations Section (Admin only) */}
          {isAdmin && invitations.length > 0 && (
            <Grid item xs={12} lg={4}>
              <Card>
                <CardContent>
                  <Typography variant="h5" component="h2" gutterBottom>
                    Pending Invitations ({invitations.length})
                  </Typography>

                  <Stack spacing={2}>
                    {invitations.map(invitation => (
                      <Paper key={invitation.id} variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="subtitle1" component="h4" gutterBottom>
                            {invitation.email}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Invited as {invitation.role}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                            Sent {formatDate(invitation.created_at)}
                            {invitation.invited_by_name && ` by ${invitation.invited_by_name}`}
                          </Typography>
                          {invitation.updated_at && invitation.updated_at !== invitation.created_at && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                              Updated {formatDate(invitation.updated_at)}
                            </Typography>
                          )}
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Chip
                            label="Pending"
                            color="warning"
                            size="small"
                            variant="outlined"
                          />
                          <Box>
                            <IconButton
                              size="small"
                              onClick={() => handleEditInvitation(invitation)}
                              color="primary"
                              title="Edit invitation"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleCancelInvitation(invitation.id)}
                              color="error"
                              title="Cancel invitation"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        </Box>
                      </Paper>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Invite Form Dialog */}
        <Dialog
          open={showInviteForm}
          onClose={resetInviteForm}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingInvitation ? 'Edit Invitation' : 'Invite Family Member'}
          </DialogTitle>
          <Box component="form" onSubmit={editingInvitation ? handleUpdateInvitation : handleInvite}>
            <DialogContent>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  value={inviteData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter email address"
                />

                <FormControl fullWidth>
                  <InputLabel>Role</InputLabel>
                  <Select
                    name="role"
                    value={inviteData.role}
                    onChange={handleInputChange}
                    label="Role"
                    required
                  >
                    <MenuItem value="member">Member</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                  </Select>
                </FormControl>

                <Typography variant="caption" color="text.secondary">
                  Admins can invite members and manage the family. Members can view and edit runbooks.
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3 }}>
              <Button onClick={resetInviteForm} variant="outlined">
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={(isInviting || isUpdating) || !inviteData.email.trim()}
              >
                {editingInvitation 
                  ? (isUpdating ? 'Updating...' : 'Update Invitation')
                  : (isInviting ? 'Sending...' : 'Send Invitation')
                }
              </Button>
            </DialogActions>
          </Box>
        </Dialog>

        {/* Cancel Invitation Confirmation Dialog */}
        <Dialog
          open={cancelInvitationDialogOpen}
          onClose={handleCancelInvitationCancel}
          aria-labelledby="cancel-invitation-dialog-title"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="cancel-invitation-dialog-title">
            Cancel Invitation
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to cancel this invitation? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleCancelInvitationCancel} variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={handleCancelInvitationConfirm} 
              variant="contained" 
              color="warning"
              autoFocus
            >
              Cancel Invitation
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Family Confirmation Dialog */}
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby="delete-family-dialog-title"
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle id="delete-family-dialog-title">
            Delete Family
          </DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete "{family.name}"? This action cannot be undone and will permanently remove all family data, runbooks, and member access.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3 }}>
            <Button onClick={handleDeleteCancel} variant="outlined">
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteConfirm} 
              variant="contained" 
              color="error"
              autoFocus
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default FamilyManagement;